import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

export const TJP_TRACK_LIMIT = 2.4;
const L_LOWER = 0.75;
const L_UPPER = 0.5;
/**
 * Punch arm visual ↔ GM hit geometry (two_joint_pole_multiplayer_gm.py).
 * Cart X uses usableHalf=220 over ±TRACK_LIMIT → px/m = 220/2.4.
 * World meters: base_offset=28/px_per_m, arm_length=78/px_per_m, cart_half=30/px_per_m.
 * GM hits when this arm segment intersects the opponent cart AABB (+ fist radius).
 */
const PUNCH_ARM_BASE_OFFSET_PX = 28;
const PUNCH_ARM_MAX_PX = 78; // max jab length at punch_state = 1
const CART_HALF_WIDTH_PX = 30;
const PUNCH_FIST_RADIUS_PX = 7;

/**
 * Dual-player visualization on a shared full-length track.
 * Player 1: blue. Player 2: orange.
 * Cart X = shared mapping across ±TRACK_LIMIT (no half-track walls).
 * Punch: linear spring arm from cart base toward opponent, length ∝ punch_state.
 * Hit shake: GM exposes players[].shake / hit_impulse — brief cart + screen jitter.
 */
export default function TwoJointPoleSceneMultiplayer({
  player1_physics = {},
  player2_physics = {},
  player1_shake = 0,
  player2_shake = 0,
  width = 560,
  height = 320,
}) {
  const [tick, setTick] = useState(0);
  const shakeActive = (Number(player1_shake) || 0) > 0.02 || (Number(player2_shake) || 0) > 0.02;

  useEffect(() => {
    if (!shakeActive) return undefined;
    let frame = 0;
    let raf;
    const loop = () => {
      frame += 1;
      setTick(frame);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [shakeActive]);

  const geo = useMemo(() => {
    const vbW = 560;
    const vbH = 320;
    const trackY = 250;
    const usableHalf = 220;
    const scale = 100;
    const trackPad = 28;

    const toCartX = (pos) => vbW / 2 + (Number(pos ?? 0) / TJP_TRACK_LIMIT) * usableHalf;

    const p1Pos = Number(player1_physics.cart_position ?? 0);
    const p2Pos = Number(player2_physics.cart_position ?? 0);
    const p1Punch = Math.max(0, Math.min(1, Number(player1_physics.punch_state) || 0));
    const p2Punch = Math.max(0, Math.min(1, Number(player2_physics.punch_state) || 0));

    const p1ShakeAmp = Math.min(10, (Number(player1_shake) || 0) * 6);
    const p2ShakeAmp = Math.min(10, (Number(player2_shake) || 0) * 6);
    const p1JitterX = p1ShakeAmp ? Math.sin(tick * 1.7) * p1ShakeAmp : 0;
    const p1JitterY = p1ShakeAmp ? Math.cos(tick * 2.1) * p1ShakeAmp * 0.45 : 0;
    const p2JitterX = p2ShakeAmp ? Math.sin(tick * 1.9 + 1) * p2ShakeAmp : 0;
    const p2JitterY = p2ShakeAmp ? Math.cos(tick * 2.3 + 0.5) * p2ShakeAmp * 0.45 : 0;
    const screenShake = Math.max(p1ShakeAmp, p2ShakeAmp) * 0.35;
    const screenDx = screenShake ? Math.sin(tick * 1.4) * screenShake : 0;
    const screenDy = screenShake ? Math.cos(tick * 1.8) * screenShake * 0.5 : 0;

    const p1_cart = { x: toCartX(p1Pos) + p1JitterX, y: trackY + p1JitterY };
    const p1_pivot = { x: p1_cart.x, y: p1_cart.y - 18 };

    const θ2_p1 = Number(player1_physics.pole2_angle) || 0;
    const θ1_p1 = Number(player1_physics.pole1_angle) || 0;
    const p1_j1 = {
      x: p1_pivot.x + Math.sin(θ2_p1) * L_LOWER * scale,
      y: p1_pivot.y - Math.cos(θ2_p1) * L_LOWER * scale,
    };
    const p1_tip = {
      x: p1_j1.x + Math.sin(θ1_p1) * L_UPPER * scale,
      y: p1_j1.y - Math.cos(θ1_p1) * L_UPPER * scale,
    };

    const p2_cart = { x: toCartX(p2Pos) + p2JitterX, y: trackY + p2JitterY };
    const p2_pivot = { x: p2_cart.x, y: p2_cart.y - 18 };

    const θ2_p2 = Number(player2_physics.pole2_angle) || 0;
    const θ1_p2 = Number(player2_physics.pole1_angle) || 0;
    const p2_j1 = {
      x: p2_pivot.x + Math.sin(θ2_p2) * L_LOWER * scale,
      y: p2_pivot.y - Math.cos(θ2_p2) * L_LOWER * scale,
    };
    const p2_tip = {
      x: p2_j1.x + Math.sin(θ1_p2) * L_UPPER * scale,
      y: p2_j1.y - Math.cos(θ1_p2) * L_UPPER * scale,
    };

    // Face opponent; default P1→right, P2→left when stacked.
    const p1Facing = p2Pos === p1Pos ? 1 : Math.sign(p2Pos - p1Pos);
    const p2Facing = p1Pos === p2Pos ? -1 : Math.sign(p1Pos - p2Pos);

    return {
      vbW,
      vbH,
      trackY,
      trackPad,
      p1_cart,
      p1_pivot,
      p1_j1,
      p1_tip,
      p1Punch,
      p1Facing,
      p2_cart,
      p2_pivot,
      p2_j1,
      p2_tip,
      p2Punch,
      p2Facing,
      screenDx,
      screenDy,
    };
  }, [player1_physics, player2_physics, player1_shake, player2_shake, tick]);

  const {
    vbW,
    vbH,
    trackY,
    trackPad,
    p1_cart,
    p1_pivot,
    p1_j1,
    p1_tip,
    p1Punch,
    p1Facing,
    p2_cart,
    p2_pivot,
    p2_j1,
    p2_tip,
    p2Punch,
    p2Facing,
    screenDx,
    screenDy,
  } = geo;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        width={width}
        height={height}
        style={{ maxWidth: '100%', display: 'block', borderRadius: 12 }}
        role="img"
        aria-label="Two-Joint Pole Multiplayer Visualization"
      >
        <defs>
          <linearGradient id="tjpSkyM" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0c1a24" />
            <stop offset="55%" stopColor="#122830" />
            <stop offset="100%" stopColor="#1a3338" />
          </linearGradient>
          <linearGradient id="tjpFloorM" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e3a3f" />
            <stop offset="100%" stopColor="#0a1214" />
          </linearGradient>
          <linearGradient id="tjpCartBlue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3f6fa6" />
            <stop offset="100%" stopColor="#1e4d7a" />
          </linearGradient>
          <linearGradient id="tjpCartOrange" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d97706" />
            <stop offset="100%" stopColor="#b45309" />
          </linearGradient>
          <filter id="tjpShadowM" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.45" />
          </filter>
        </defs>

        <g transform={`translate(${screenDx}, ${screenDy})`}>
          <rect width={vbW} height={vbH} fill="url(#tjpSkyM)" />

          <rect x="0" y={trackY + 8} width={vbW} height={vbH - trackY - 8} fill="url(#tjpFloorM)" />

          {/* Soft center marker only — not a wall */}
          <line
            x1={vbW / 2}
            y1={trackY - 14}
            x2={vbW / 2}
            y2={trackY + 6}
            stroke="#6ee7d8"
            strokeWidth="1.5"
            strokeDasharray="3,5"
            opacity="0.35"
          />
          <text x={vbW / 2} y={20} textAnchor="middle" fill="#888" fontSize="14" fontWeight="bold">
            P1 vs P2
          </text>

          {/* Shared full-length track */}
          <rect x={trackPad} y={trackY - 2} width={vbW - trackPad * 2} height="6" rx="2" fill="#3d555c" />
          <rect x={trackPad} y={trackY} width={vbW - trackPad * 2} height="3" fill="#6ee7d8" opacity="0.3" />
          <rect x={trackPad - 8} y={trackY - 10} width="8" height="22" rx="2" fill="#4ECDC4" opacity="0.7" />
          <rect x={vbW - trackPad} y={trackY - 10} width="8" height="22" rx="2" fill="#f59e0b" opacity="0.7" />

          {/* --- PLAYER 1 (BLUE) --- */}
          <ellipse cx={p1_cart.x} cy={p1_cart.y + 10} rx="36" ry="5" fill="#000" opacity="0.35" />

          <PunchArm
            cartX={p1_cart.x}
            cartY={p1_cart.y}
            facing={p1Facing}
            punchState={p1Punch}
            stroke="#93c5fd"
            fist="#60a5fa"
            spring="#4ECDC4"
          />

          <g filter="url(#tjpShadowM)">
            <rect
              x={p1_cart.x - CART_HALF_WIDTH_PX}
              y={p1_cart.y - 20}
              width={CART_HALF_WIDTH_PX * 2}
              height="20"
              rx="3"
              fill="url(#tjpCartBlue)"
              stroke="#6b9ec3"
              strokeWidth="1.5"
            />
            <circle cx={p1_cart.x - 18} cy={p1_cart.y + 2} r="6" fill="#1a1a1a" stroke="#4ECDC4" strokeWidth="1.5" />
            <circle cx={p1_cart.x + 18} cy={p1_cart.y + 2} r="6" fill="#1a1a1a" stroke="#4ECDC4" strokeWidth="1.5" />
            <circle cx={p1_cart.x - 18} cy={p1_cart.y + 2} r="2" fill="#6ee7d8" />
            <circle cx={p1_cart.x + 18} cy={p1_cart.y + 2} r="2" fill="#6ee7d8" />
          </g>

          <line x1={p1_pivot.x} y1={p1_pivot.y} x2={p1_j1.x} y2={p1_j1.y} stroke="#1e7fb8" strokeWidth="7" strokeLinecap="round" />
          <line x1={p1_pivot.x} y1={p1_pivot.y} x2={p1_j1.x} y2={p1_j1.y} stroke="#4db8e8" strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />
          <line x1={p1_j1.x} y1={p1_j1.y} x2={p1_tip.x} y2={p1_tip.y} stroke="#60a5fa" strokeWidth="6" strokeLinecap="round" />
          <line x1={p1_j1.x} y1={p1_j1.y} x2={p1_tip.x} y2={p1_tip.y} stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
          <circle cx={p1_pivot.x} cy={p1_pivot.y} r="6" fill="#0a1214" stroke="#4ECDC4" strokeWidth="2.5" />
          <circle cx={p1_j1.x} cy={p1_j1.y} r="5" fill="#0a1214" stroke="#1e7fb8" strokeWidth="2.5" />
          <circle cx={p1_tip.x} cy={p1_tip.y} r="4" fill="#0a1214" stroke="#60a5fa" strokeWidth="2" />
          <circle cx={p1_tip.x} cy={p1_tip.y} r="8" fill="#60a5fa" opacity="0.2" />

          <text x={Math.max(40, Math.min(vbW - 40, p1_cart.x))} y={vbH - 12} textAnchor="middle" fill="#60a5fa" fontSize="12" fontWeight="bold">
            P1
          </text>

          {/* --- PLAYER 2 (ORANGE) --- */}
          <ellipse cx={p2_cart.x} cy={p2_cart.y + 10} rx="36" ry="5" fill="#000" opacity="0.35" />

          <PunchArm
            cartX={p2_cart.x}
            cartY={p2_cart.y}
            facing={p2Facing}
            punchState={p2Punch}
            stroke="#fdba74"
            fist="#f59e0b"
            spring="#fbbf24"
          />

          <g filter="url(#tjpShadowM)">
            <rect
              x={p2_cart.x - CART_HALF_WIDTH_PX}
              y={p2_cart.y - 20}
              width={CART_HALF_WIDTH_PX * 2}
              height="20"
              rx="3"
              fill="url(#tjpCartOrange)"
              stroke="#f59e0b"
              strokeWidth="1.5"
            />
            <circle cx={p2_cart.x - 18} cy={p2_cart.y + 2} r="6" fill="#1a1a1a" stroke="#f59e0b" strokeWidth="1.5" />
            <circle cx={p2_cart.x + 18} cy={p2_cart.y + 2} r="6" fill="#1a1a1a" stroke="#f59e0b" strokeWidth="1.5" />
            <circle cx={p2_cart.x - 18} cy={p2_cart.y + 2} r="2" fill="#fbbf24" />
            <circle cx={p2_cart.x + 18} cy={p2_cart.y + 2} r="2" fill="#fbbf24" />
          </g>

          <line x1={p2_pivot.x} y1={p2_pivot.y} x2={p2_j1.x} y2={p2_j1.y} stroke="#d97706" strokeWidth="7" strokeLinecap="round" />
          <line x1={p2_pivot.x} y1={p2_pivot.y} x2={p2_j1.x} y2={p2_j1.y} stroke="#fdba74" strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />
          <line x1={p2_j1.x} y1={p2_j1.y} x2={p2_tip.x} y2={p2_tip.y} stroke="#f59e0b" strokeWidth="6" strokeLinecap="round" />
          <line x1={p2_j1.x} y1={p2_j1.y} x2={p2_tip.x} y2={p2_tip.y} stroke="#fcd34d" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
          <circle cx={p2_pivot.x} cy={p2_pivot.y} r="6" fill="#0a1214" stroke="#f59e0b" strokeWidth="2.5" />
          <circle cx={p2_j1.x} cy={p2_j1.y} r="5" fill="#0a1214" stroke="#d97706" strokeWidth="2.5" />
          <circle cx={p2_tip.x} cy={p2_tip.y} r="4" fill="#0a1214" stroke="#f59e0b" strokeWidth="2" />
          <circle cx={p2_tip.x} cy={p2_tip.y} r="8" fill="#f59e0b" opacity="0.2" />

          <text x={Math.max(40, Math.min(vbW - 40, p2_cart.x))} y={vbH - 12} textAnchor="middle" fill="#f59e0b" fontSize="12" fontWeight="bold">
            P2
          </text>
        </g>
      </svg>
    </Box>
  );
}

/** Linear spring jab from cart base toward opponent (length matches GM PUNCH_ARM_*). */
function PunchArm({ cartX, cartY, facing, punchState, stroke, fist, spring }) {
  if (!punchState || punchState < 0.02 || !facing) return null;

  const baseX = cartX + facing * PUNCH_ARM_BASE_OFFSET_PX;
  const y = cartY - 11;
  const len = punchState * PUNCH_ARM_MAX_PX;
  const tipX = baseX + facing * len;
  const coils = 4;
  const coilPts = [];
  for (let i = 0; i <= coils; i += 1) {
    const t = i / coils;
    const x = baseX + facing * len * t;
    const amp = (i % 2 === 0 ? 1 : -1) * 3.5 * (1 - t * 0.35);
    coilPts.push(`${x},${y + amp}`);
  }

  return (
    <g aria-hidden>
      <polyline
        points={coilPts.join(' ')}
        fill="none"
        stroke={spring}
        strokeWidth="2"
        opacity="0.75"
        strokeLinejoin="round"
      />
      <line
        x1={baseX}
        y1={y}
        x2={tipX}
        y2={y}
        stroke={stroke}
        strokeWidth="5"
        strokeLinecap="round"
        opacity="0.95"
      />
      <circle cx={tipX} cy={y} r={PUNCH_FIST_RADIUS_PX} fill={fist} stroke="#0a1214" strokeWidth="1.5" />
      <circle cx={tipX} cy={y} r={PUNCH_FIST_RADIUS_PX + 4} fill={fist} opacity="0.22" />
    </g>
  );
}
